import { ref, computed, nextTick } from "vue";
import { getArtistProfilePhotoSrc } from "~/utils";
import { ENABLE_ARTWORK_SHARED_TRANSITION } from "~/constants";

// Global client state for currently transitioning artist
const activeArtist = ref<any | null>(null);
const activeArtistId = ref<string | null>(null);
const activeArtistSlug = ref<string | null>(null);
const activeCardKey = ref<string | null>(null);
const isTransitionActive = ref(false);
const isTransitionReady = ref(false);
let cleanupTimeout: any = null;

export function preloadArtistHiRes(photoName?: string) {
  if (!ENABLE_ARTWORK_SHARED_TRANSITION || !import.meta.client || !photoName) return;
  try {
    const url = getArtistProfilePhotoSrc(photoName, 1920);
    const img = new Image();
    img.src = url;
  } catch (_) {}
}

export function useArtistViewTransition() {
  const supportsViewTransitions = () => {
    if (!ENABLE_ARTWORK_SHARED_TRANSITION) return false;
    if (!import.meta.client) return false;
    if (!("startViewTransition" in document)) return false;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
    return true;
  };

  /**
   * Pre-binds the view-transition-name on pointerdown so the DOM snapshot captures it reliably
   * and preloads the hi-res image
   */
  const prepareArtistTransition = (artist: any, cardKey?: string) => {
    if (!supportsViewTransitions()) return;
    if (cleanupTimeout) clearTimeout(cleanupTimeout);

    const idStr = artist?.id != null ? String(artist.id) : "";
    const slugStr = artist?.slug != null ? String(artist.slug) : null;
    const photo = artist?.photo_name;

    if (import.meta.dev) {
      console.log(`[Artist Transition] click (t=${Math.round(performance.now())}ms)`, {
        id: idStr,
        slug: slugStr,
        cardKey,
        thumbnailAvailable: photo ? getArtistProfilePhotoSrc(photo, 360) : false,
      });
    }

    if (photo) {
      preloadArtistHiRes(photo);
    }

    activeArtist.value = artist;
    activeArtistId.value = idStr;
    activeArtistSlug.value = slugStr;
    activeCardKey.value = cardKey || idStr;
    isTransitionActive.value = true;
    isTransitionReady.value = false;

    // Safety fallback cleanup in case view transition was cancelled
    cleanupTimeout = setTimeout(() => {
      clearTransition();
    }, 3000);
  };

  /**
   * Triggers a shared element view transition when navigating to an artist detail page.
   */
  const startArtistTransition = async (
    artist: any,
    navigate: () => Promise<any> | any,
    cardKey?: string
  ) => {
    prepareArtistTransition(artist, cardKey);
    await nextTick();
    if (import.meta.dev) {
      console.log(`[Artist Transition] start (t=${Math.round(performance.now())}ms)`);
    }
    return await navigate();
  };

  /**
   * Called by the Nuxt view-transition client plugin when startViewTransition executes.
   */
  const onTransitionStart = (transition: any) => {
    if (!isTransitionActive.value) return;
    if (import.meta.dev) {
      console.log(`[Artist Transition] startViewTransition (t=${Math.round(performance.now())}ms)`);
    }
    if (transition?.updateCallbackDone) {
      transition.updateCallbackDone
        .then(() => {
          if (import.meta.dev) {
            console.log(`[Artist Transition] updateCallbackDone (t=${Math.round(performance.now())}ms)`);
          }
        })
        .catch(() => {});
    }
    if (transition?.ready) {
      transition.ready
        .then(() => {
          isTransitionReady.value = true;
          if (import.meta.dev) {
            console.log(`[Artist Transition] ready (t=${Math.round(performance.now())}ms)`);
          }
        })
        .catch(() => {});
    }
    if (transition?.finished) {
      transition.finished
        .then(() => {
          if (import.meta.dev) {
            console.log(`[Artist Transition] finished (t=${Math.round(performance.now())}ms)`);
          }
          clearTransition();
        })
        .catch(() => {
          clearTransition();
        });
    }
  };

  /**
   * Checks if this specific artist/card is currently part of an active transition on the listing page.
   */
  const isArtistActive = (artistOrIdentifier: any, cardKey?: string) => {
    if (!artistOrIdentifier || !isTransitionActive.value) return false;
    if (cardKey && activeCardKey.value) {
      return cardKey === activeCardKey.value;
    }
    if (typeof artistOrIdentifier === "object") {
      const id = artistOrIdentifier.id != null ? String(artistOrIdentifier.id) : null;
      const slug = artistOrIdentifier.slug != null ? String(artistOrIdentifier.slug) : null;
      return Boolean(
        (id && (activeArtistId.value === id || activeArtistSlug.value === id)) ||
        (slug && (activeArtistId.value === slug || activeArtistSlug.value === slug))
      );
    }
    const str = String(artistOrIdentifier);
    return Boolean(
      (activeArtistId.value && activeArtistId.value === str) ||
      (activeArtistSlug.value && activeArtistSlug.value === str)
    );
  };

  /**
   * Returns inline style assigning view-transition-name for source listing cards.
   */
  const getTransitionStyle = (artistOrIdentifier: any, cardKey?: string) => {
    if (isArtistActive(artistOrIdentifier, cardKey)) {
      return {
        viewTransitionName: "artist-hero-transition",
        "view-transition-name": "artist-hero-transition",
      };
    }
    return {};
  };

  /**
   * Stable transition style for the single destination hero image on the detail page.
   */
  const getHeroTransitionStyle = () => {
    if (isTransitionActive.value) {
      return {
        viewTransitionName: "artist-hero-transition",
        "view-transition-name": "artist-hero-transition",
      };
    }
    return {};
  };

  /**
   * Returns preloaded artist data if available during transition
   */
  const getPrefetchedArtist = (slugOrId?: string) => {
    if (!activeArtist.value) return undefined;
    const a = activeArtist.value;
    if (!slugOrId) return a;
    const str = String(slugOrId);
    if (String(a.id) === str || a.slug === str || !a.slug) {
      return {
        ...a,
        photo_name: a.photo_name || "",
        photo_width: a.photo_width || 1200,
        photo_height: a.photo_height || 900,
      };
    }
    return undefined;
  };

  const clearTransition = () => {
    if (cleanupTimeout) clearTimeout(cleanupTimeout);
    if (import.meta.dev && isTransitionActive.value) {
      console.log(`[Artist Transition] cleanup (t=${Math.round(performance.now())}ms)`);
    }
    activeArtistId.value = null;
    activeArtistSlug.value = null;
    activeArtist.value = null;
    activeCardKey.value = null;
    isTransitionActive.value = false;
    isTransitionReady.value = false;
  };

  return {
    activeArtistId: computed(() => activeArtistId.value),
    activeArtistSlug: computed(() => activeArtistSlug.value),
    isTransitionActive: computed(() => isTransitionActive.value),
    isTransitionReady: computed(() => isTransitionReady.value),
    supportsViewTransitions,
    prepareArtistTransition,
    startArtistTransition,
    onTransitionStart,
    preloadArtistHiRes,
    getTransitionStyle,
    getHeroTransitionStyle,
    isArtistActive,
    getPrefetchedArtist,
    clearTransition,
  };
}
